import React from "react";
import {
  Typography,
  Card,
  CardContent,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { PAYOUT_HISTORY } from "../../PaymentSetup.constants";
import {
  ScrollableTableContainer,
  PayoutTable,
  AmountCell,
  TransferCardPaper,
  TransferCardGrid,
  MobileListBox,
  TransferAmountText,
} from "./PayoutHistoryCard.styles";

const getStatusColor = (status) => {
  if (status === "completed") return "success";
  if (status === "reversed") return "error";
  return "default";
};

const TableView = ({ transfers }) => (
  <ScrollableTableContainer component={Paper} variant="outlined">
    <PayoutTable size="small">
      <TableHead>
        <TableRow>
          <TableCell>{PAYOUT_HISTORY.COLUMN_PERIOD}</TableCell>
          <TableCell>{PAYOUT_HISTORY.COLUMN_CLIENT}</TableCell>
          <TableCell align="right">{PAYOUT_HISTORY.COLUMN_GROSS}</TableCell>
          <TableCell align="right">
            {PAYOUT_HISTORY.COLUMN_YOUR_SHARE}
          </TableCell>
          <TableCell>{PAYOUT_HISTORY.COLUMN_TIER}</TableCell>
          <TableCell>{PAYOUT_HISTORY.COLUMN_STATUS}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {transfers.map((transfer) => (
          <TableRow key={transfer.id}>
            <TableCell>
              {transfer.periodStart} – {transfer.periodEnd}
            </TableCell>
            <TableCell>
              {transfer.payment?.patientName || PAYOUT_HISTORY.PATIENT_NA}
            </TableCell>
            <TableCell align="right">
              ${Number(transfer.grossAmount).toFixed(2)}
            </TableCell>
            <AmountCell align="right">
              ${Number(transfer.amount).toFixed(2)}
            </AmountCell>
            <TableCell>
              <Chip
                label={`T${transfer.tier}`}
                size="small"
                variant="outlined"
              />
            </TableCell>
            <TableCell>
              <Chip
                label={transfer.status}
                size="small"
                color={getStatusColor(transfer.status)}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </PayoutTable>
  </ScrollableTableContainer>
);

const TransferCard = ({ transfer }) => (
  <TransferCardPaper variant="outlined">
    <TransferCardGrid>
      <Typography variant="caption" color="text.secondary">
        {PAYOUT_HISTORY.COLUMN_PERIOD}
      </Typography>
      <Typography variant="body2">
        {transfer.periodStart} – {transfer.periodEnd}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {PAYOUT_HISTORY.COLUMN_CLIENT}
      </Typography>
      <Typography variant="body2">
        {transfer.payment?.patientName || PAYOUT_HISTORY.PATIENT_NA}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {PAYOUT_HISTORY.COLUMN_GROSS}
      </Typography>
      <Typography variant="body2">
        ${Number(transfer.grossAmount).toFixed(2)}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {PAYOUT_HISTORY.COLUMN_YOUR_SHARE}
      </Typography>
      <TransferAmountText variant="body2">
        ${Number(transfer.amount).toFixed(2)}
      </TransferAmountText>

      <Typography variant="caption" color="text.secondary">
        {PAYOUT_HISTORY.COLUMN_TIER}
      </Typography>
      <Box>
        <Chip label={`T${transfer.tier}`} size="small" variant="outlined" />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {PAYOUT_HISTORY.COLUMN_STATUS}
      </Typography>
      <Box>
        <Chip
          label={transfer.status}
          size="small"
          color={getStatusColor(transfer.status)}
        />
      </Box>
    </TransferCardGrid>
  </TransferCardPaper>
);

const PayoutHistoryCard = ({ transfers }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!transfers?.length) return null;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {PAYOUT_HISTORY.TITLE}
        </Typography>
        {isMobile ? (
          <MobileListBox>
            {transfers.map((transfer) => (
              <TransferCard key={transfer.id} transfer={transfer} />
            ))}
          </MobileListBox>
        ) : (
          <TableView transfers={transfers} />
        )}
      </CardContent>
    </Card>
  );
};

export default PayoutHistoryCard;
